import { useEffect, useRef, useState } from "react";

/**
 * TechnoCursor — Vinci "Golden Arrow" cursor system (v2).
 *
 * • Brushed-gold (#D4AF37) sharp geometric arrow tracks the pointer.
 * • Bioluminescent trail · every ~40 ms a fading gold dot is left
 *   behind, giving a subtle "meteor" tail without saturating the UI.
 * • **Feb 2026 refinement** — the "Glowing Gold Target" interaction
 *   circle was removed per Executive Command. The arrow + tail carry
 *   the entire cursor language now.
 * • **Butter-smooth scrolling** — pointer x/y are cached and written
 *   inside a `requestAnimationFrame` loop, so scroll-driven repaints
 *   never race the arrow transform. `will-change: transform` hints
 *   the compositor to keep the arrow on its own GPU layer.
 * • Mobile fallback · disabled on touch / coarse-pointer devices and
 *   on prefers-reduced-motion. Native cursor stays.
 * • Text inputs keep their caret cursor so typing works naturally.
 */
const GOLD = "#D4AF37";
const GOLD_LIGHT = "#F6E4A6";

export function TechnoCursor() {
  const arrowRef = useRef(null);
  const trailRef = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || calm) return;
    setEnabled(true);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const style = document.createElement("style");
    style.setAttribute("data-vinci-cursor", "true");
    style.innerHTML = `
      html, body, * { cursor: none !important; }
      input, textarea, [contenteditable="true"] { cursor: text !important; }
    `;
    document.head.appendChild(style);

    // Cached pointer position — mousemove writes here, rAF reads it.
    // Decoupling the write from the read keeps scroll-triggered
    // repaints from stealing frames from the transform update.
    let x = window.innerWidth / 2, y = window.innerHeight / 2;
    let lastTrail = 0;
    let running = true;

    const move = (e) => { x = e.clientX; y = e.clientY; };
    const hide = () => { if (arrowRef.current) arrowRef.current.style.opacity = "0"; };
    const show = () => { if (arrowRef.current) arrowRef.current.style.opacity = "1"; };

    const tick = () => {
      if (!running) return;
      if (arrowRef.current) {
        arrowRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      }
      const now = performance.now();
      if (trailRef.current && now - lastTrail > 35) {
        lastTrail = now;
        const dot = document.createElement("span");
        dot.style.cssText = `
          position: absolute; left: ${x}px; top: ${y}px;
          width: 4px; height: 4px; margin: -2px 0 0 -2px;
          border-radius: 50%; background: ${GOLD};
          box-shadow: 0 0 10px rgba(212,175,55,0.55);
          opacity: 0.7; pointer-events: none;
          transition: opacity 550ms linear, transform 550ms linear;
          will-change: opacity, transform;
        `;
        trailRef.current.appendChild(dot);
        requestAnimationFrame(() => {
          dot.style.opacity = "0";
          dot.style.transform = "scale(0.35)";
        });
        setTimeout(() => dot.remove(), 600);
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    // Passive listeners so scroll compositing isn't blocked.
    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("mouseleave", hide);
    window.addEventListener("mouseenter", show);

    return () => {
      running = false;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseleave", hide);
      window.removeEventListener("mouseenter", show);
      style.remove();
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      {/* Trail canvas · absolute, hosts fading gold dots */}
      <div
        ref={trailRef}
        aria-hidden
        data-testid="techno-cursor-trail"
        className="pointer-events-none fixed inset-0 z-[9997]"
        style={{ mixBlendMode: "screen" }}
      />

      {/* Sharp geometric golden arrow */}
      <svg
        ref={arrowRef}
        aria-hidden
        data-testid="techno-cursor-arrow"
        width="22" height="22" viewBox="0 0 22 22"
        className="pointer-events-none fixed top-0 left-0 z-[9999]"
        style={{
          transform: "translate3d(-100px, -100px, 0)",
          willChange: "transform",
          filter: `drop-shadow(0 0 6px rgba(212,175,55,0.85)) drop-shadow(0 0 14px rgba(212,175,55,0.45))`,
        }}
      >
        <defs>
          <linearGradient id="vinciCursorGold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"  stopColor="#7A5410" />
            <stop offset="45%" stopColor={GOLD} />
            <stop offset="100%" stopColor={GOLD_LIGHT} />
          </linearGradient>
          {/* Brushed effect via a subtle diagonal stripe pattern */}
          <pattern id="vinciBrush" x="0" y="0" width="4" height="4"
                    patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <rect width="4" height="4" fill="url(#vinciCursorGold)" />
            <line x1="0" y1="0" x2="0" y2="4"
                  stroke="rgba(0,0,0,0.10)" strokeWidth="0.6" />
          </pattern>
        </defs>
        {/* Sharp geometric arrow head — classic cursor angle at 45° */}
        <path
          d="M1 1 L1 19 L6.2 14.4 L9.8 21 L12.6 19.6 L9 13 L15.6 12.6 Z"
          fill="url(#vinciBrush)"
          stroke="#0A0906" strokeWidth="0.8" strokeLinejoin="miter"
        />
        {/* Bright inner highlight edge */}
        <path
          d="M1 1 L1 19"
          stroke={GOLD_LIGHT} strokeWidth="0.6" opacity="0.85"
        />
      </svg>
    </>
  );
}
